import React, { useEffect, useMemo, useRef, useState } from "react";
import { getAuth } from "firebase/auth";
import {
  addDoc,
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  documentId,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "./firebase";

/**
 * Trelli BudgetBucketsPage (V2)
 * Data model (future Trelli):
 * - households/{householdId}
 * - households/{householdId}/members/{uid}  { role, canManageAllBudgets }
 * - households/{householdId}/budgetPlans/{planId} { status: draft|active|archived }
 * - households/{householdId}/budgetPlans/{planId}/buckets/{bucketId}  (flat buckets)
 * - households/{householdId}/budgetMonths/{YYYY-MM}
 * - households/{householdId}/budgetMonths/{YYYY-MM}/bucketSnapshots/{bucketId}
 * - households/{householdId}/auditEvents/{eventId}
 */

/* ──────────────────────────────────────────────────────────────────────────────
   Helpers
────────────────────────────────────────────────────────────────────────────── */
function getMonthKey(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}
function monthKeyToDateStart(monthKey) {
  const [y, m] = monthKey.split("-").map((x) => parseInt(x, 10));
  return new Date(y, m - 1, 1);
}
function monthKeyToDateEnd(monthKey) {
  const [y, m] = monthKey.split("-").map((x) => parseInt(x, 10));
  return new Date(y, m, 0); // last day of month
}
function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}
function fmtDateShort(d) {
  if (!d) return "";
  const dt = typeof d === "string" ? new Date(d) : d;
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
function fmtMoney(n) {
  const val = Number(n || 0);
  return val.toLocaleString(undefined, { style: "currency", currency: "USD" });
}
function clamp01(x) {
  const n = Number(x);
  if (!isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}
function norm(s) {
  return (s ?? "").toString().trim().toLowerCase();
}
function safeIdFromMonthKey(monthKey) {
  // Firestore doc id can be "YYYY-MM" fine.
  return monthKey;
}
function downloadTextFile(filename, content, mime = "text/plain") {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
function sortAlpha(a, b) {
  return (a || "").toString().localeCompare((b || "").toString(), undefined, { numeric: true });
}

/* ──────────────────────────────────────────────────────────────────────────────
   Planned amount + reset windows (calendar-accurate for weekly/biweekly)
────────────────────────────────────────────────────────────────────────────── */
function countOccurrencesInMonth({ monthKey, anchorDateISO, stepDays }) {
  const monthStart = monthKeyToDateStart(monthKey);
  const monthEnd = monthKeyToDateEnd(monthKey);

  const anchor = anchorDateISO ? new Date(anchorDateISO) : monthStart;
  // We want the first occurrence on/after monthStart, aligned to anchor.
  // Find k such that anchor + k*stepDays >= monthStart.
  let k = 0;
  if (anchor < monthStart) {
    const diffDays = Math.floor((monthStart - anchor) / (1000 * 60 * 60 * 24));
    k = Math.ceil(diffDays / stepDays);
  }
  let first = addDays(anchor, k * stepDays);
  // If we somehow landed before monthStart due to DST math, bump forward.
  while (first < monthStart) first = addDays(first, stepDays);

  let count = 0;
  let cur = first;
  while (cur <= monthEnd) {
    count += 1;
    cur = addDays(cur, stepDays);
  }
  return count;
}

function computePlannedAmountForMonth(bucket, monthKey) {
  if (!bucket || !monthKey) return 0;

  const group = norm(bucket.group);
  const cadence = norm(bucket.cadence);

  // Savings goals: monthly planned is monthlyAmount (or derived elsewhere)
  if (group === "savings_goal") {
    const m = Number(bucket.monthlyAmount ?? 0) || 0;
    return m;
  }

  // Income/Expense:
  if (cadence === "monthly") return Number(bucket.amount ?? 0) || 0;

  if (cadence === "custom_monthly") {
    const custom = bucket.customByMonth || {};
    const v = custom?.[monthKey];
    const fallback = Number(bucket.amount ?? 0) || 0;
    return Number(v ?? fallback) || 0;
  }

  if (cadence === "semimonthly") {
    // Convention: amount stored per period OR per month. We’ll treat it as per period * 2
    // If you later decide amount is per month, flip this to just amount.
    const perPeriod = Number(bucket.amount ?? 0) || 0;
    return perPeriod * 2;
  }

  if (cadence === "weekly") {
    const per = Number(bucket.amount ?? 0) || 0;
    const anchor = bucket.anchorDate || `${monthKey}-01`;
    const c = countOccurrencesInMonth({ monthKey, anchorDateISO: anchor, stepDays: 7 });
    return per * c;
  }

  if (cadence === "biweekly") {
    const per = Number(bucket.amount ?? 0) || 0;
    const anchor = bucket.anchorDate || `${monthKey}-01`;
    const c = countOccurrencesInMonth({ monthKey, anchorDateISO: anchor, stepDays: 14 });
    return per * c;
  }

  // Default
  return Number(bucket.amount ?? 0) || 0;
}

function computeResetText(bucket, monthKey) {
  const cadence = norm(bucket?.cadence);
  const monthStart = monthKeyToDateStart(monthKey);

  if (cadence === "monthly" || cadence === "custom_monthly") {
    // Reset on 1st
    const next = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 1);
    return `Resets ${fmtDateShort(next)}`;
  }

  if (cadence === "semimonthly") {
    // Reset on 1st/15th next boundary from today
    const now = new Date();
    const inSameMonth = now.getFullYear() === monthStart.getFullYear() && now.getMonth() === monthStart.getMonth();
    const d = inSameMonth ? now.getDate() : 1;

    if (d < 15) return `Resets ${fmtDateShort(new Date(monthStart.getFullYear(), monthStart.getMonth(), 15))}`;
    return `Resets ${fmtDateShort(new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 1))}`;
  }

  if (cadence === "weekly" || cadence === "biweekly") {
    const step = cadence === "weekly" ? 7 : 14;
    const anchorISO = bucket.anchorDate || `${monthKey}-01`;
    const anchor = new Date(anchorISO);

    // Find current/next window end relative to "now"
    const now = new Date();
    let k = 0;
    if (anchor < now) {
      const diffDays = Math.floor((now - anchor) / (1000 * 60 * 60 * 24));
      k = Math.floor(diffDays / step);
    }
    const windowStart = addDays(anchor, k * step);
    const windowEnd = addDays(windowStart, step);
    return `Resets ${fmtDateShort(windowEnd)}`;
  }

  return "";
}

/* ──────────────────────────────────────────────────────────────────────────────
   Permission helpers
────────────────────────────────────────────────────────────────────────────── */
function rolePriority(role) {
  const r = norm(role);
  if (r === "head") return 4;
  if (r === "admin") return 3;
  if (r === "spouse") return 2;
  if (r === "member") return 1;
  if (r === "child") return 0;
  return 0;
}
function canManageAll(member) {
  if (!member) return false;
  if (member?.canManageAllBudgets === true) return true;
  return rolePriority(member?.role) >= 3; // head/admin
}
function bucketPerms(bucket) {
  const p = bucket?.permissions || {};
  return {
    viewUids: Array.isArray(p.viewUids) ? p.viewUids : [],
    spendUids: Array.isArray(p.spendUids) ? p.spendUids : [],
    manageUids: Array.isArray(p.manageUids) ? p.manageUids : [],
    approveBailoutUids: Array.isArray(p.approveBailoutUids) ? p.approveBailoutUids : [],
  };
}
function canViewBucket(bucket, uid, member) {
  if (!bucket) return false;
  if (!uid) return false;
  if (canManageAll(member)) return true;
  const p = bucketPerms(bucket);
  return p.viewUids.includes(uid) || p.manageUids.includes(uid) || p.spendUids.includes(uid);
}

/* ──────────────────────────────────────────────────────────────────────────────
   UI primitives (simple, clean)
────────────────────────────────────────────────────────────────────────────── */
const COLORS = {
  trelli: "#00B074",
  trelliSoft: "rgba(0,176,116,0.10)",
  border: "rgba(0,0,0,0.08)",
  text: "rgba(0,0,0,0.86)",
  subtext: "rgba(0,0,0,0.58)",
  bg: "#ffffff",
  soft: "rgba(0,0,0,0.03)",
  danger: "#B00020",
};

function Card({ children, style }) {
  return (
    <div
      style={{
        background: COLORS.bg,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 12,
        boxShadow: "0 1px 0 rgba(0,0,0,0.04)",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function Button({ children, onClick, variant = "primary", disabled, style, title }) {
  const base = {
    borderRadius: 10,
    padding: "10px 12px",
    fontSize: 13,
    border: `1px solid ${COLORS.border}`,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.55 : 1,
    userSelect: "none",
    whiteSpace: "nowrap",
  };

  let v = {};
  if (variant === "primary") {
    v = { background: COLORS.trelli, color: "#fff", borderColor: "transparent" };
  } else if (variant === "ghost") {
    v = { background: "transparent", color: COLORS.text };
  } else if (variant === "danger") {
    v = { background: "#fff", color: COLORS.danger, borderColor: "rgba(176,0,32,0.25)" };
  } else if (variant === "soft") {
    v = { background: COLORS.trelliSoft, color: COLORS.text, borderColor: "transparent" };
  }

  return (
    <button disabled={disabled} onClick={disabled ? undefined : onClick} style={{ ...base, ...v, ...style }} title={title}>
      {children}
    </button>
  );
}

function Input({ value, onChange, placeholder, style, type = "text" }) {
  return (
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange?.(e.target.value)}
      style={{
        width: "100%",
        borderRadius: 10,
        padding: "10px 12px",
        fontSize: 13,
        border: `1px solid ${COLORS.border}`,
        outline: "none",
        ...style,
      }}
    />
  );
}

function Select({ value, onChange, options, style, disabled }) {
  return (
    <select
      disabled={disabled}
      value={value}
      onChange={(e) => onChange?.(e.target.value)}
      style={{
        width: "100%",
        borderRadius: 10,
        padding: "10px 12px",
        fontSize: 13,
        border: `1px solid ${COLORS.border}`,
        background: disabled ? "rgba(0,0,0,0.02)" : "#fff",
        outline: "none",
        ...style,
      }}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value} disabled={o.disabled}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function Modal({ open, title, children, onClose, footer }) {
  if (!open) return null;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 18,
        zIndex: 9999,
      }}
      onMouseDown={onClose}
    >
      <div
        style={{
          width: "min(920px, 100%)",
          maxHeight: "85vh",
          overflow: "auto",
          background: "#fff",
          borderRadius: 14,
          border: `1px solid ${COLORS.border}`,
          boxShadow: "0 10px 35px rgba(0,0,0,0.22)",
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div style={{ padding: 16, borderBottom: `1px solid ${COLORS.border}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>{title}</div>
            <Button variant="ghost" onClick={onClose} style={{ padding: "8px 10px" }}>
              Close
            </Button>
          </div>
        </div>
        <div style={{ padding: 16 }}>{children}</div>
        {footer ? <div style={{ padding: 16, borderTop: `1px solid ${COLORS.border}` }}>{footer}</div> : null}
      </div>
    </div>
  );
}

function ProgressBar({ value01 }) {
  const v = clamp01(value01);
  return (
    <div style={{ height: 10, background: "rgba(0,0,0,0.06)", borderRadius: 999, overflow: "hidden" }}>
      <div style={{ height: 10, width: `${v * 100}%`, background: COLORS.trelli }} />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────────
   Main component
────────────────────────────────────────────────────────────────────────────── */
export default function BudgetBucketsPage() {
  const auth = getAuth();

  const [user, setUser] = useState(null);

  // Household selection
  const [households, setHouseholds] = useState([]); // [{id, type, name, memberRole, canManageAllBudgets}]
  const [householdId, setHouseholdId] = useState("");
  const [member, setMember] = useState(null);

  // Plan selection
  const [activePlan, setActivePlan] = useState(null); // {id, ...}
  const [draftPlans, setDraftPlans] = useState([]);
  const [planId, setPlanId] = useState(""); // active or selected draft
  const [planStatus, setPlanStatus] = useState("active"); // active|draft

  // Month selection
  const [monthKey, setMonthKey] = useState(() => getMonthKey());
  const monthOptions = useMemo(() => {
    // Current + past 18 months
    const opts = [];
    const now = new Date();
    for (let i = 0; i < 24; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const k = getMonthKey(d);
      opts.push({ value: k, label: k });
    }
    return opts;
  }, []);

  // Data
  const [buckets, setBuckets] = useState([]);
  const [snapshots, setSnapshots] = useState([]); // [{id: bucketId, ...}]
  const snapshotsById = useMemo(() => {
    const m = new Map();
    for (const s of snapshots) m.set(s.id, s);
    return m;
  }, [snapshots]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Search
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);

  // Mass selection
  const [selectedIds, setSelectedIds] = useState([]);

  // Modals
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardMode, setWizardMode] = useState("create"); // create|edit
  const [wizardBucket, setWizardBucket] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailBucket, setDetailBucket] = useState(null);

  // Drafts
  const [draftsOpen, setDraftsOpen] = useState(false);
  const [activating, setActivating] = useState(false);

  // Ensure auth
  useEffect(() => {
    const unsub = auth.onAuthStateChanged((u) => setUser(u));
    return () => unsub();
  }, [auth]);

  // Load households for user via collectionGroup on members/{uid}
  useEffect(() => {
    let cancelled = false;
    async function loadHouseholds() {
      if (!user?.uid) return;
      setLoading(true);
      setError("");

      try {
        const q = query(collectionGroup(db, "members"), where("memberUid", "==", user.uid));
        const snap = await getDocs(q);

        if (cancelled) return;

        const list = [];
        for (const d of snap.docs) {
          // d.ref: households/{householdId}/members/{uid}
          const memberData = d.data() || {};
          const householdRef = d.ref.parent?.parent; // households/{householdId}
          if (!householdRef) continue;

          const hSnap = await getDoc(householdRef);
          const hData = hSnap.exists() ? hSnap.data() : {};

          list.push({
            id: householdRef.id,
            type: hData?.type || "adult",
            name: hData?.name || (hData?.type === "child" ? "Child household" : "Household"),
            memberRole: memberData?.role || "member",
            canManageAllBudgets:
              memberData?.canManageAllBudgets === true || rolePriority(memberData?.role) >= 3,
          });
        }

        // Prefer adult household first
        list.sort((a, b) => {
          if (a.type !== b.type) return a.type === "adult" ? -1 : 1;
          // higher role first
          return rolePriority(b.memberRole) - rolePriority(a.memberRole);
        });

        setHouseholds(list);

        // Choose default if not set
        if (!householdId && list.length > 0) {
          setHouseholdId(list[0].id);
        }

        setLoading(false);
      } catch (e) {
        console.error(e);
        setError("Failed to load households.");
        setLoading(false);
      }
    }
    loadHouseholds();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  // Load member doc for selected household
  useEffect(() => {
    let cancelled = false;
    async function loadMember() {
      if (!user?.uid || !householdId) return;
      try {
        const ref = doc(db, `households/${householdId}/members/${user.uid}`);
        const snap = await getDoc(ref);
        if (cancelled) return;
        setMember(snap.exists() ? snap.data() : null);
      } catch (e) {
        console.error(e);
        setMember(null);
      }
    }
    loadMember();
    return () => {
      cancelled = true;
    };
  }, [user?.uid, householdId]);

  // Subscribe to budget plans (active + drafts)
  useEffect(() => {
    if (!householdId) return;

    const plansRef = collection(db, `households/${householdId}/budgetPlans`);

    const qActive = query(plansRef, where("status", "==", "active"), limit(1));
    const qDrafts = query(plansRef, where("status", "==", "draft"), orderBy("updatedAt", "desc"));

    const unsubA = onSnapshot(
      qActive,
      (snap) => {
        const a = snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() };
        setActivePlan(a);

        // If we are currently in "active" view, keep planId aligned
        setPlanStatus((prev) => {
          if (prev === "active") {
            setPlanId(a?.id || "");
          }
          return prev;
        });

        // If nothing selected yet, default to active
        setPlanId((prev) => prev || a?.id || "");
      },
      (err) => console.error("active plan snapshot error:", err)
    );

    const unsubD = onSnapshot(
      qDrafts,
      (snap) => {
        const drafts = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setDraftPlans(drafts);
      },
      (err) => console.error("draft plans snapshot error:", err)
    );

    return () => {
      unsubA();
      unsubD();
    };
  }, [householdId]);

  // Keep planId in sync when planStatus changes
  useEffect(() => {
    if (planStatus === "active") {
      setPlanId(activePlan?.id || "");
    }
  }, [planStatus, activePlan?.id]);

  // Subscribe to buckets in selected plan
  useEffect(() => {
    if (!householdId || !planId || !user?.uid) return;

    setLoading(true);

    const bucketsRef = collection(db, `households/${householdId}/budgetPlans/${planId}/buckets`);
    const qBuckets = query(bucketsRef, orderBy("name", "asc"));

    const unsub = onSnapshot(
      qBuckets,
      (snap) => {
        const raw = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        // Filter by permission
        const filtered = raw.filter((b) => canViewBucket(b, user.uid, member));
        setBuckets(filtered);
        setLoading(false);
      },
      (err) => {
        console.error("buckets snapshot error:", err);
        setError("Failed to load buckets.");
        setLoading(false);
      }
    );

    return () => unsub();
  }, [householdId, planId, user?.uid, member]);

  // Subscribe snapshots for month
  useEffect(() => {
    if (!householdId || !monthKey) return;
    const monthRef = doc(db, `households/${householdId}/budgetMonths/${safeIdFromMonthKey(monthKey)}`);

    // Ensure month doc exists (lightweight)
    (async () => {
      try {
        const s = await getDoc(monthRef);
        if (!s.exists()) {
          await setDoc(
            monthRef,
            {
              activePlanId: activePlan?.id || null,
              totals: {
                incomePlanned: 0,
                incomeActual: 0,
                savingsPlanned: 0,
                savingsActual: 0,
                expensePlanned: 0,
                expenseActual: 0,
              },
              updatedAt: serverTimestamp(),
              createdAt: serverTimestamp(),
            },
            { merge: true }
          );
        }
      } catch (e) {
        console.error("ensure month doc failed:", e);
      }
    })();

    const snapsRef = collection(db, `households/${householdId}/budgetMonths/${safeIdFromMonthKey(monthKey)}/bucketSnapshots`);
    const qSnaps = query(snapsRef);

    const unsub = onSnapshot(
      qSnaps,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setSnapshots(list);
      },
      (err) => console.error("snapshots snapshot error:", err)
    );

    return () => unsub();
  }, [householdId, monthKey, activePlan?.id]);

  // Derived: decorate buckets with planned + actual + utilization
  const decorated = useMemo(() => {
    const list = buckets.map((b) => {
      const planned = computePlannedAmountForMonth(b, monthKey);
      const snap = snapshotsById.get(b.id);
      const actual = Number(snap?.actualToDate ?? 0) || 0;

      const group = norm(b.group);
      const used = Math.abs(actual); // expenses likely negative elsewhere; snapshot should be positive sums
      const denom = planned === 0 ? 1 : planned;
      const fill = clamp01(used / denom);

      let subText = "";
      const resetText = computeResetText(b, monthKey);

      if (group === "income") {
        const remaining = planned - actual;
        subText = remaining > 0 ? `${fmtMoney(remaining)} until achieved` : `Achieved (+${fmtMoney(Math.abs(remaining))})`;
      } else if (group === "expense") {
        const remaining = planned - actual;
        subText = remaining >= 0 ? `${fmtMoney(remaining)} remaining` : `Over by ${fmtMoney(Math.abs(remaining))}`;
      } else if (group === "savings_goal") {
        // Progress total is handled separately (goalAmount)
        const goal = Number(b.goalAmount ?? 0) || 0;
        const totalAlloc = Number(snap?.totalAllocated ?? 0) || 0;
        const rem = goal - totalAlloc;
        subText = goal > 0 ? (rem > 0 ? `${fmtMoney(rem)} to goal` : `Goal reached`) : "";
      }

      return {
        ...b,
        plannedAmountForMonth: planned,
        actualToDate: actual,
        utilizationFill01: fill,
        utilizationSubText: subText,
        resetText,
        isInactive: b.isActive === false,
      };
    });

    const income = list.filter((b) => norm(b.group) === "income").sort((a, b) => sortAlpha(a.name, b.name));
    const savings = list.filter((b) => norm(b.group) === "savings_goal").sort((a, b) => sortAlpha(a.name, b.name));
    const expense = list.filter((b) => norm(b.group) === "expense").sort((a, b) => sortAlpha(a.name, b.name));

    return { income, savings, expense };
  }, [buckets, monthKey, snapshotsById]);

  const summaryTotals = useMemo(() => {
    const sum = (arr, k) => arr.reduce((acc, x) => acc + (Number(x?.[k] ?? 0) || 0), 0);

    const incomePlanned = sum(decorated.income, "plannedAmountForMonth");
    const incomeActual = sum(decorated.income, "actualToDate");

    const savingsPlanned = sum(decorated.savings, "plannedAmountForMonth");
    // Savings actual could be from transfers; snapshot field optional
    const savingsActual = decorated.savings.reduce((acc, b) => acc + (Number(snapshotsById.get(b.id)?.actualToDate ?? 0) || 0), 0);

    const expensePlanned = sum(decorated.expense, "plannedAmountForMonth");
    const expenseActual = sum(decorated.expense, "actualToDate");

    return {
      incomePlanned,
      incomeActual,
      savingsPlanned,
      savingsActual,
      expensePlanned,
      expenseActual,
    };
  }, [decorated, snapshotsById]);

  // Search typeahead
  const searchResults = useMemo(() => {
    const q = norm(search);
    if (!q) return [];
    const all = [...decorated.income, ...decorated.savings, ...decorated.expense];
    return all
      .filter((b) => norm(b.name).includes(q))
      .slice(0, 12);
  }, [search, decorated]);

  // Scroll jump refs
  const incomeRef = useRef(null);
  const savingsRef = useRef(null);
  const expenseRef = useRef(null);

  function scrollToSection(key) {
    const map = { income: incomeRef, savings: savingsRef, expense: expenseRef };
    const r = map[key]?.current;
    if (r) r.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  // Draft actions
  async function createDraftFromActive() {
    if (!householdId || !activePlan?.id || !user?.uid) return;
    if (!canManageAll(member)) return;

    try {
      const activeBucketsRef = collection(db, `households/${householdId}/budgetPlans/${activePlan.id}/buckets`);
      const activeBucketsSnap = await getDocs(query(activeBucketsRef, orderBy("name", "asc")));

      const newPlanRef = await addDoc(collection(db, `households/${householdId}/budgetPlans`), {
        status: "draft",
        title: `Draft (${new Date().toLocaleDateString()})`,
        createdBy: user.uid,
        updatedBy: user.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const batch = writeBatch(db);

      for (const d of activeBucketsSnap.docs) {
        const data = d.data();
        const newBucketRef = doc(db, `households/${householdId}/budgetPlans/${newPlanRef.id}/buckets/${d.id}`);
        batch.set(newBucketRef, {
          ...data,
          createdAt: data.createdAt || serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }

      // audit event
      const auditRef = doc(collection(db, `households/${householdId}/auditEvents`));
      batch.set(auditRef, {
        type: "budgetPlan.createDraft",
        createdBy: user.uid,
        createdAt: serverTimestamp(),
        meta: { fromActivePlanId: activePlan.id, newDraftPlanId: newPlanRef.id },
      });

      await batch.commit();
      setDraftsOpen(true);
    } catch (e) {
      console.error(e);
      setError("Failed to create draft.");
    }
  }

  async function activateDraft(draftPlanId) {
    if (!householdId || !draftPlanId || !user?.uid) return;
    if (!canManageAll(member)) return;

    setActivating(true);
    try {
      // Archive existing active plan (if any), activate draft
      const batch = writeBatch(db);

      if (activePlan?.id) {
        batch.update(doc(db, `households/${householdId}/budgetPlans/${activePlan.id}`), {
          status: "archived",
          updatedBy: user.uid,
          updatedAt: serverTimestamp(),
        });
      }

      batch.update(doc(db, `households/${householdId}/budgetPlans/${draftPlanId}`), {
        status: "active",
        activeFromMonth: monthKey,
        updatedBy: user.uid,
        updatedAt: serverTimestamp(),
      });

      const auditRef = doc(collection(db, `households/${householdId}/auditEvents`));
      batch.set(auditRef, {
        type: "budgetPlan.activateDraft",
        createdBy: user.uid,
        createdAt: serverTimestamp(),
        meta: { activatedDraftPlanId: draftPlanId, archivedPlanId: activePlan?.id || null, monthKey },
      });

      await batch.commit();

      // Switch UI back to active
      setPlanStatus("active");
      setDraftsOpen(false);
    } catch (e) {
      console.error(e);
      setError("Failed to activate draft.");
    } finally {
      setActivating(false);
    }
  }

  /* ──────────────────────────────────────────────────────────────────────────────
     Writes: upsert snapshot + totals cache (lightweight client-side)
  ─────────────────────────────────────────────────────────────────────────────── */
  async function upsertBucketSnapshot({ bucket, monthKeyLocal }) {
    if (!householdId || !bucket?.id || !monthKeyLocal) return;

    const planned = computePlannedAmountForMonth(bucket, monthKeyLocal);

    const snapRef = doc(
      db,
      `households/${householdId}/budgetMonths/${safeIdFromMonthKey(monthKeyLocal)}/bucketSnapshots/${bucket.id}`
    );

    // Keep existing actualToDate if present (do not overwrite actuals here)
    const existing = snapshotsById.get(bucket.id);
    const existingActual = Number(existing?.actualToDate ?? 0) || 0;
    const existingTotalAllocated = Number(existing?.totalAllocated ?? 0) || 0;

    await setDoc(
      snapRef,
      {
        bucketId: bucket.id,
        bucketName: bucket.name || "",
        group: bucket.group || "",
        plannedAmountForMonth: planned,
        actualToDate: existingActual,
        totalAllocated: existingTotalAllocated,
        resetText: computeResetText(bucket, monthKeyLocal),
        sourcePlanId: planId || null,
        sourceBucketId: bucket.id,
        updatedAt: serverTimestamp(),
        createdAt: existing?.createdAt || serverTimestamp(),
      },
      { merge: true }
    );
  }

  async function refreshMonthTotals() {
    if (!householdId || !monthKey) return;

    // compute from currently decorated + snapshots in memory (best-effort)
    const totals = {
      incomePlanned: summaryTotals.incomePlanned,
      incomeActual: summaryTotals.incomeActual,
      savingsPlanned: summaryTotals.savingsPlanned,
      savingsActual: summaryTotals.savingsActual,
      expensePlanned: summaryTotals.expensePlanned,
      expenseActual: summaryTotals.expenseActual,
    };

    const monthRef = doc(db, `households/${householdId}/budgetMonths/${safeIdFromMonthKey(monthKey)}`);
    await setDoc(
      monthRef,
      {
        activePlanId: activePlan?.id || null,
        totals,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  }

  /* ──────────────────────────────────────────────────────────────────────────────
     Bucket Wizard state + save
  ─────────────────────────────────────────────────────────────────────────────── */
  const WIZ_STEPS = ["Type", "Details", "Schedule", "Permissions", "Funding", "Review"];

  function makeDefaultPermissions() {
    // defaults based on member role
    const uid = user?.uid;
    const canAll = canManageAll(member);
    if (!uid) return { viewUids: [], spendUids: [], manageUids: [], approveBailoutUids: [] };

    // If head/admin: manage + view + approve; spend optional
    if (canAll) {
      return { viewUids: [uid], spendUids: [uid], manageUids: [uid], approveBailoutUids: [uid] };
    }

    // spouse/member default: view only on buckets they create
    return { viewUids: [uid], spendUids: [], manageUids: [uid], approveBailoutUids: [] };
  }

  function openCreateWizard() {
    setWizardMode("create");
    setWizardBucket({
      group: "",
      name: "",
      isActive: true,

      // income/expense schedule
      cadence: "monthly",
      anchorDate: `${monthKey}-01`,
      amount: 0,
      customByMonth: {},

      // savings goal
      goalAmount: 0,
      goalDate: "",
      monthlyAmount: 0,

      // funding
      fundingType: "external_only",
      externalAccountIds: [],
      fundingLinks: { trelliAccountId: null, virtualCardId: null },

      // permissions
      permissions: makeDefaultPermissions(),
    });
    setWizardOpen(true);
    setWizardStep(0);
  }

  function openEditWizard(bucket) {
    setWizardMode("edit");
    setWizardBucket({
      ...bucket,
      permissions: bucketPerms(bucket),
      anchorDate: bucket.anchorDate || `${monthKey}-01`,
      fundingType: bucket.fundingType || "external_only",
      externalAccountIds: Array.isArray(bucket.externalAccountIds) ? bucket.externalAccountIds : [],
      fundingLinks: bucket.fundingLinks || { trelliAccountId: null, virtualCardId: null },
      customByMonth: bucket.customByMonth || {},
    });
    setWizardOpen(true);
    setWizardStep(1); // start at Details for edit
  }

  const [wizardStep, setWizardStep] = useState(0);

  const wizardErrors = useMemo(() => {
    const b = wizardBucket || {};
    const errs = [];

    if (wizardStep >= 0) {
      if (!norm(b.group)) errs.push("Select a budget type.");
    }
    if (wizardStep >= 1) {
      if (!norm(b.name)) errs.push("Bucket name is required.");
      if (norm(b.group) !== "savings_goal") {
        if (Number(b.amount ?? 0) <= 0 && norm(b.cadence) !== "custom_monthly") errs.push("Amount must be greater than 0.");
      }
      if (norm(b.group) === "savings_goal") {
        // Need at least two of three: goalAmount, goalDate, monthlyAmount
        const hasGoal = Number(b.goalAmount ?? 0) > 0;
        const hasDate = !!b.goalDate;
        const hasMonthly = Number(b.monthlyAmount ?? 0) > 0;
        const count = [hasGoal, hasDate, hasMonthly].filter(Boolean).length;
        if (count < 2) errs.push("Savings goal needs at least two of: Goal Amount, Goal Date, Monthly Amount.");
      }
    }
    if (wizardStep >= 2) {
      const c = norm(b.cadence);
      const g = norm(b.group);
      if (g !== "savings_goal") {
        if (!["weekly", "biweekly", "semimonthly", "monthly", "custom_monthly"].includes(c)) errs.push("Choose a valid cadence.");
        if ((c === "weekly" || c === "biweekly") && !b.anchorDate) errs.push("Anchor date is required for weekly/bi-weekly.");
        if (c === "custom_monthly") {
          // ok if map is partially filled; allow fallback to amount
        }
      }
    }
    if (wizardStep >= 3) {
      const p = bucketPerms(b);
      if (!p.viewUids?.length) errs.push("At least one viewer is required.");
      if (!p.manageUids?.length) errs.push("At least one manager is required.");
    }

    return errs;
  }, [wizardBucket, wizardStep]);

  function deriveSavingsFields(bucketDraft) {
    // Given any two, derive the third (simple monthly granularity)
    const b = { ...bucketDraft };
    const hasGoal = Number(b.goalAmount ?? 0) > 0;
    const hasMonthly = Number(b.monthlyAmount ?? 0) > 0;
    const hasDate = !!b.goalDate;

    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);

    if (hasGoal && hasDate && !hasMonthly) {
      const end = new Date(b.goalDate);
      const months =
        (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1;
      const m = months <= 0 ? 0 : Number(b.goalAmount) / months;
      b.monthlyAmount = Math.round(m * 100) / 100;
      return b;
    }

    if (hasGoal && hasMonthly && !hasDate) {
      const months = Math.ceil(Number(b.goalAmount) / Number(b.monthlyAmount));
      const end = new Date(start.getFullYear(), start.getMonth() + Math.max(1, months) - 1, 1);
      // set to end-of-month
      const endDate = new Date(end.getFullYear(), end.getMonth() + 1, 0);
      b.goalDate = endDate.toISOString().slice(0, 10);
      return b;
    }

    if (hasDate && hasMonthly && !hasGoal) {
      const end = new Date(b.goalDate);
      const months =
        (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1;
      const g = months <= 0 ? 0 : Number(b.monthlyAmount) * months;
      b.goalAmount = Math.round(g * 100) / 100;
      return b;
    }

    return b;
  }

  async function saveWizard() {
    if (!householdId || !planId || !user?.uid) return;
    if (wizardErrors.length) return;

    const b = wizardBucket || {};
    const now = serverTimestamp();

    try {
      // derive savings goal fields
      let payload = { ...b };
      if (norm(payload.group) === "savings_goal") {
        payload = deriveSavingsFields(payload);
      }

      // Normalize payload
      const clean = {
        group: payload.group,
        name: payload.name,
        isActive: payload.isActive !== false,

        cadence: payload.group === "savings_goal" ? "monthly" : payload.cadence,
        anchorDate: payload.anchorDate || `${monthKey}-01`,
        semimonthlyDays: payload.cadence === "semimonthly" ? [1, 15] : undefined,

        amount: payload.group === "savings_goal" ? Number(payload.monthlyAmount ?? 0) || 0 : Number(payload.amount ?? 0) || 0,
        customByMonth: payload.cadence === "custom_monthly" ? (payload.customByMonth || {}) : {},

        goalAmount: payload.group === "savings_goal" ? Number(payload.goalAmount ?? 0) || 0 : undefined,
        goalDate: payload.group === "savings_goal" ? payload.goalDate || "" : undefined,
        monthlyAmount: payload.group === "savings_goal" ? Number(payload.monthlyAmount ?? 0) || 0 : undefined,

        fundingType: payload.fundingType || "external_only",
        externalAccountIds: Array.isArray(payload.externalAccountIds) ? payload.externalAccountIds : [],
        fundingLinks: payload.fundingLinks || { trelliAccountId: null, virtualCardId: null },

        permissions: bucketPerms(payload),

        updatedAt: now,
      };

      if (wizardMode === "create") {
        clean.createdAt = now;
        clean.createdBy = user.uid;

        const ref = await addDoc(collection(db, `households/${householdId}/budgetPlans/${planId}/buckets`), clean);

        // snapshot best-effort
        await upsertBucketSnapshot({ bucket: { id: ref.id, ...clean }, monthKeyLocal: monthKey });

        // audit
        await addDoc(collection(db, `households/${householdId}/auditEvents`), {
          type: "bucket.create",
          createdBy: user.uid,
          createdAt: serverTimestamp(),
          meta: { planId, bucketId: ref.id, group: clean.group, monthKey },
        });
      } else {
        // edit
        const bucketId = payload.id;
        if (!bucketId) throw new Error("Missing bucket id for edit.");

        await updateDoc(doc(db, `households/${householdId}/budgetPlans/${planId}/buckets/${bucketId}`), clean);

        await upsertBucketSnapshot({ bucket: { id: bucketId, ...clean }, monthKeyLocal: monthKey });

        await addDoc(collection(db, `households/${householdId}/auditEvents`), {
          type: "bucket.update",
          createdBy: user.uid,
          createdAt: serverTimestamp(),
          meta: { planId, bucketId, monthKey },
        });
      }

      await refreshMonthTotals();
      setWizardOpen(false);
    } catch (e) {
      console.error(e);
      setError("Failed to save bucket.");
    }
  }

  /* ──────────────────────────────────────────────────────────────────────────────
     Mass actions
  ─────────────────────────────────────────────────────────────────────────────── */
  const selectedBuckets = useMemo(() => {
    const set = new Set(selectedIds);
    const all = [...decorated.income, ...decorated.savings, ...decorated.expense];
    return all.filter((b) => set.has(b.id));
  }, [selectedIds, decorated]);

  function toggleSelected(id) {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }
  function clearSelected() {
    setSelectedIds([]);
  }
  function selectAllInSection(sectionKey) {
    const ids = (decorated?.[sectionKey] || []).map((b) => b.id);
    setSelectedIds((prev) => Array.from(new Set([...prev, ...ids])));
  }

  async function archiveSelected() {
    if (!householdId || !planId || !user?.uid) return;
    if (!canManageAll(member)) return;

    try {
      const batch = writeBatch(db);
      for (const b of selectedBuckets) {
        batch.update(doc(db, `households/${householdId}/budgetPlans/${planId}/buckets/${b.id}`), {
          isActive: false,
          updatedAt: serverTimestamp(),
        });
      }
      const auditRef = doc(collection(db, `households/${householdId}/auditEvents`));
      batch.set(auditRef, {
        type: "bucket.massArchive",
        createdBy: user.uid,
        createdAt: serverTimestamp(),
        meta: { planId, bucketIds: selectedBuckets.map((x) => x.id), monthKey },
      });
      await batch.commit();
      clearSelected();
    } catch (e) {
      console.error(e);
      setError("Failed to archive selected.");
    }
  }

  function exportSelectedCSV() {
    const rows = [
      ["Bucket ID", "Group", "Name", "Cadence", "Planned (Month)", "Actual (Month-to-date)", "Funding Type", "Active"],
      ...selectedBuckets.map((b) => [
        b.id,
        b.group,
        b.name,
        b.cadence,
        b.plannedAmountForMonth,
        b.actualToDate,
        b.fundingType,
        b.isActive !== false ? "true" : "false",
      ]),
    ];

    const csv = rows.map((r) => r.map((x) => `"${String(x ?? "").replaceAll('"', '""')}"`).join(",")).join("\n");
    downloadTextFile(`trelli-buckets-${monthKey}.csv`, csv, "text/csv");
  }

  const [tempIncOpen, setTempIncOpen] = useState(false);
  const [tempIncAmount, setTempIncAmount] = useState("");

  async function applyTempIncrease() {
    if (!householdId || !monthKey || !user?.uid) return;
    if (!selectedBuckets.length) return;
    if (!canManageAll(member)) return;

    const addAmt = Number(tempIncAmount);
    if (!isFinite(addAmt) || addAmt === 0) {
      setError("Enter a valid temporary increase amount.");
      return;
    }

    try {
      const batch = writeBatch(db);
      for (const b of selectedBuckets) {
        const snapRef = doc(
          db,
          `households/${householdId}/budgetMonths/${safeIdFromMonthKey(monthKey)}/bucketSnapshots/${b.id}`
        );
        // Ensure snapshot exists
        batch.set(
          snapRef,
          {
            bucketId: b.id,
            bucketName: b.name,
            group: b.group,
            plannedAmountForMonth: b.plannedAmountForMonth,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );

        batch.set(
          snapRef,
          {
            overrides: {
              tempIncrease: {
                amount: addAmt,
                appliedAt: serverTimestamp(),
                appliedBy: user.uid,
              },
            },
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }

      const auditRef = doc(collection(db, `households/${householdId}/auditEvents`));
      batch.set(auditRef, {
        type: "bucket.massTempIncrease",
        createdBy: user.uid,
        createdAt: serverTimestamp(),
        meta: { planId, monthKey, bucketIds: selectedBuckets.map((x) => x.id), amount: addAmt },
      });

      await batch.commit();
      setTempIncOpen(false);
      setTempIncAmount("");
      clearSelected();
    } catch (e) {
      console.error(e);
      setError("Failed to apply temporary increase.");
    }
  }

  /* ──────────────────────────────────────────────────────────────────────────────
     UI: rows + sections
  ─────────────────────────────────────────────────────────────────────────────── */
  function FundingBadge({ label, muted }) {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          padding: "4px 8px",
          borderRadius: 999,
          fontSize: 12,
          border: `1px solid ${COLORS.border}`,
          background: muted ? "rgba(0,0,0,0.02)" : COLORS.trelliSoft,
          color: COLORS.text,
          marginRight: 6,
        }}
      >
        {label}
      </span>
    );
  }

  function fundingSummary(bucket) {
    const t = norm(bucket.fundingType);
    if (t === "external_only") return [<FundingBadge key="ext" label="External" muted />];
    if (t === "account_bucket")
      return [
        <FundingBadge key="acct" label="Trelli Account" />,
        <FundingBadge key="ext" label="External (optional)" muted />,
      ];
    if (t === "card_bucket")
      return [
        <FundingBadge key="vc" label="Virtual Card" />,
        <FundingBadge key="pc" label="Physical capable" muted />,
      ];
    return [<FundingBadge key="unknown" label="Unassigned" muted />];
  }

  function RowKebab({ onView, onEdit, onToggleActive }) {
    return (
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Button variant="ghost" onClick={onView} style={{ padding: "6px 10px" }}>
          View
        </Button>
        <Button variant="ghost" onClick={onEdit} style={{ padding: "6px 10px" }}>
          Edit
        </Button>
        <Button variant="ghost" onClick={onToggleActive} style={{ padding: "6px 10px" }}>
          Toggle
        </Button>
      </div>
    );
  }

  async function toggleBucketActive(bucket) {
    if (!householdId || !planId || !user?.uid) return;
    if (!canManageAll(member)) return;

    try {
      await updateDoc(doc(db, `households/${householdId}/budgetPlans/${planId}/buckets/${bucket.id}`), {
        isActive: bucket.isActive === false ? true : false,
        updatedAt: serverTimestamp(),
      });

      await addDoc(collection(db, `households/${householdId}/auditEvents`), {
        type: "bucket.toggleActive",
        createdBy: user.uid,
        createdAt: serverTimestamp(),
        meta: { planId, bucketId: bucket.id, isActive: bucket.isActive === false, monthKey },
      });
    } catch (e) {
      console.error(e);
      setError("Failed to toggle bucket.");
    }
  }

  function BucketRow({ bucket, variant }) {
    const isSavings = variant === "savings";
    const isIncome = variant === "income";
    const isExpense = variant === "expense";

    const inactiveStyle = bucket.isInactive
      ? { opacity: 0.55, filter: "grayscale(0.15)" }
      : {};

    // Apply temp increase override to display planned amount (display only)
    const snap = snapshotsById.get(bucket.id);
    const tempInc = Number(snap?.overrides?.tempIncrease?.amount ?? 0) || 0;
    const plannedDisplayed = Number(bucket.plannedAmountForMonth ?? 0) + tempInc;

    // Utilization
    let fill = bucket.utilizationFill01;
    if (plannedDisplayed > 0) {
      fill = clamp01((Number(bucket.actualToDate ?? 0) || 0) / plannedDisplayed);
    }

    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isSavings
            ? "44px 2.1fr 2.0fr 1.1fr 1.1fr 1.1fr 1.5fr 1.4fr"
            : "44px 2.1fr 2.0fr 1.1fr 1.1fr 1.1fr 1.5fr 1.4fr",
          alignItems: "center",
          gap: 12,
          padding: "10px 12px",
          borderBottom: `1px solid ${COLORS.border}`,
          ...inactiveStyle,
        }}
      >
        <div>
          <input
            type="checkbox"
            checked={selectedIds.includes(bucket.id)}
            onChange={() => toggleSelected(bucket.id)}
            style={{ width: 16, height: 16 }}
          />
        </div>

        <div style={{ minWidth: 0 }}>
          <div
            style={{ fontSize: 13, fontWeight: 650, color: COLORS.text, cursor: "pointer" }}
            onClick={() => {
              setDetailBucket(bucket);
              setDetailOpen(true);
            }}
            title="Open details"
          >
            {bucket.name || "Untitled"}
          </div>
          <div style={{ fontSize: 12, color: COLORS.subtext, marginTop: 2 }}>
            {bucket.isInactive ? "Off list" : "Active"}
          </div>
        </div>

        <div style={{ minWidth: 0 }}>
          <ProgressBar value01={fill} />
          <div style={{ fontSize: 12, color: COLORS.subtext, marginTop: 6, display: "flex", justifyContent: "space-between" }}>
            <span>{bucket.utilizationSubText}</span>
            <span>{bucket.resetText}</span>
          </div>
        </div>

        {/* Planned */}
        <div style={{ fontSize: 13, color: COLORS.text, textAlign: "right" }}>
          {fmtMoney(plannedDisplayed)}
          {tempInc !== 0 ? (
            <div style={{ fontSize: 12, color: COLORS.subtext }}>+ temp {fmtMoney(tempInc)}</div>
          ) : null}
        </div>

        {/* Actual */}
        <div style={{ fontSize: 13, color: COLORS.text, textAlign: "right" }}>
          {isIncome ? fmtMoney(bucket.actualToDate) : fmtMoney(bucket.actualToDate)}
          <div style={{ fontSize: 12, color: COLORS.subtext }}>{isIncome ? "Received" : isSavings ? "Allocated" : "Spent"}</div>
        </div>

        {/* Funding */}
        <div style={{ fontSize: 12 }}>{fundingSummary(bucket)}</div>

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <RowKebab
            onView={() => {
              setDetailBucket(bucket);
              setDetailOpen(true);
            }}
            onEdit={() => openEditWizard(bucket)}
            onToggleActive={() => toggleBucketActive(bucket)}
          />
        </div>
      </div>
    );
  }

  function Section({ title, sectionKey, headerCols, children, stickyRef }) {
    return (
      <div ref={stickyRef}>
        <div
          style={{
            position: "sticky",
            top: 146, // below the top sticky zone (summary + controls). tuned to our layout below.
            zIndex: 50,
            background: COLORS.trelliSoft,
            borderTop: `1px solid ${COLORS.border}`,
            borderBottom: `1px solid ${COLORS.border}`,
            padding: "10px 12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 800, color: COLORS.text }}>{title}</div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button variant="soft" onClick={() => selectAllInSection(sectionKey)} style={{ padding: "8px 10px" }}>
              Select all
            </Button>
            <Button variant="ghost" onClick={() => scrollToSection(sectionKey)} style={{ padding: "8px 10px" }}>
              Jump
            </Button>
          </div>
        </div>

        <div
          style={{
            position: "sticky",
            top: 190,
            zIndex: 49,
            background: "#fff",
            borderBottom: `1px solid ${COLORS.border}`,
            padding: "10px 12px",
            display: "grid",
            gridTemplateColumns: headerCols,
            alignItems: "center",
            gap: 12,
            fontSize: 12,
            color: COLORS.subtext,
            fontWeight: 700,
          }}
        >
          <div />
          <div>Bucket Name</div>
          <div>Utilization</div>
          <div style={{ textAlign: "right" }}>Budgeted</div>
          <div style={{ textAlign: "right" }}>{title === "Income" ? "Received" : title === "Savings Goals" ? "Allocated" : "Spent"}</div>
          <div>Funded</div>
          <div style={{ textAlign: "right" }}>More</div>
        </div>

        {children}
      </div>
    );
  }

  /* ──────────────────────────────────────────────────────────────────────────────
     Detail modal
  ─────────────────────────────────────────────────────────────────────────────── */
  const detailSnapshot = useMemo(() => {
    if (!detailBucket) return null;
    return snapshotsById.get(detailBucket.id) || null;
  }, [detailBucket, snapshotsById]);

  function DetailModal() {
    const b = detailBucket;
    if (!b) return null;

    const p = bucketPerms(b);
    const tempInc = Number(detailSnapshot?.overrides?.tempIncrease?.amount ?? 0) || 0;

    return (
      <Modal
        open={detailOpen}
        title={`Bucket Details — ${b.name || "Untitled"}`}
        onClose={() => setDetailOpen(false)}
        footer={
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Button variant="ghost" onClick={() => openEditWizard(b)}>
                Edit bucket
              </Button>
              <Button variant="ghost" onClick={() => toggleBucketActive(b)} disabled={!canManageAll(member)}>
                {b.isInactive ? "Activate" : "Move off list"}
              </Button>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <Button variant="primary" onClick={() => setDetailOpen(false)}>
                Done
              </Button>
            </div>
          </div>
        }
      >
        <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 14 }}>
          <Card style={{ padding: 14 }}>
            <div style={{ fontSize: 12, color: COLORS.subtext, fontWeight: 700 }}>Overview</div>
            <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: COLORS.subtext }}>Type</span>
                <span style={{ fontWeight: 650 }}>{b.group}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: COLORS.subtext }}>Cadence</span>
                <span style={{ fontWeight: 650 }}>{b.cadence}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: COLORS.subtext }}>Planned this month</span>
                <span style={{ fontWeight: 650 }}>{fmtMoney(b.plannedAmountForMonth + tempInc)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: COLORS.subtext }}>Actual to date</span>
                <span style={{ fontWeight: 650 }}>{fmtMoney(b.actualToDate)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: COLORS.subtext }}>Reset</span>
                <span style={{ fontWeight: 650 }}>{b.resetText || "-"}</span>
              </div>
            </div>
          </Card>

          <Card style={{ padding: 14 }}>
            <div style={{ fontSize: 12, color: COLORS.subtext, fontWeight: 700 }}>Funding</div>
            <div style={{ marginTop: 10 }}>
              <div style={{ marginBottom: 10 }}>{fundingSummary(b)}</div>
              <div style={{ fontSize: 12, color: COLORS.subtext }}>
                Trelli account/card creation will be wired when issuing is enabled. External assignments already work through
                transaction categorization.
              </div>
            </div>
          </Card>

          <Card style={{ padding: 14 }}>
            <div style={{ fontSize: 12, color: COLORS.subtext, fontWeight: 700 }}>Permissions</div>
            <div style={{ marginTop: 10, display: "grid", gap: 8, fontSize: 13 }}>
              <div>
                <span style={{ color: COLORS.subtext }}>View:</span>{" "}
                <span style={{ fontWeight: 650 }}>{p.viewUids?.length ? p.viewUids.join(", ") : "-"}</span>
              </div>
              <div>
                <span style={{ color: COLORS.subtext }}>Spend:</span>{" "}
                <span style={{ fontWeight: 650 }}>{p.spendUids?.length ? p.spendUids.join(", ") : "-"}</span>
              </div>
              <div>
                <span style={{ color: COLORS.subtext }}>Manage:</span>{" "}
                <span style={{ fontWeight: 650 }}>{p.manageUids?.length ? p.manageUids.join(", ") : "-"}</span>
              </div>
              <div>
                <span style={{ color: COLORS.subtext }}>Approve bailout:</span>{" "}
                <span style={{ fontWeight: 650 }}>{p.approveBailoutUids?.length ? p.approveBailoutUids.join(", ") : "-"}</span>
              </div>
            </div>
          </Card>

          <Card style={{ padding: 14 }}>
            <div style={{ fontSize: 12, color: COLORS.subtext, fontWeight: 700 }}>Transactions</div>
            <div style={{ marginTop: 10, fontSize: 12, color: COLORS.subtext }}>
              Hook: filter your existing Transactions by bucketId once that mapping is finalized in your tx pipeline.
            </div>
          </Card>
        </div>
      </Modal>
    );
  }

  /* ──────────────────────────────────────────────────────────────────────────────
     Wizard modal
  ─────────────────────────────────────────────────────────────────────────────── */
  function WizardStepper() {
    return (
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {WIZ_STEPS.map((s, idx) => {
          const active = idx === wizardStep;
          return (
            <div
              key={s}
              style={{
                padding: "6px 10px",
                borderRadius: 999,
                fontSize: 12,
                border: `1px solid ${active ? "transparent" : COLORS.border}`,
                background: active ? COLORS.trelliSoft : "#fff",
                fontWeight: active ? 800 : 650,
                color: COLORS.text,
              }}
            >
              {idx + 1}. {s}
            </div>
          );
        })}
      </div>
    );
  }

  function WizardTypeStep() {
    const g = norm(wizardBucket?.group);
    const setGroup = (group) => setWizardBucket((prev) => ({ ...prev, group }));

    const CardChoice = ({ label, value, desc }) => {
      const selected = g === value;
      return (
        <div
          onClick={() => setGroup(value)}
          style={{
            padding: 14,
            borderRadius: 12,
            border: `1px solid ${selected ? "transparent" : COLORS.border}`,
            background: selected ? COLORS.trelliSoft : "#fff",
            cursor: "pointer",
          }}
        >
          <div style={{ fontWeight: 800, color: COLORS.text }}>{label}</div>
          <div style={{ marginTop: 6, fontSize: 12, color: COLORS.subtext }}>{desc}</div>
        </div>
      );
    };

    return (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
        <CardChoice label="Income" value="income" desc="Money coming in. Tracks progress until achieved." />
        <CardChoice label="Savings Goal" value="savings_goal" desc="A goal with a target amount and timeline." />
        <CardChoice label="Expense" value="expense" desc="Money going out. Tracks remaining until reset." />
      </div>
    );
  }

  function WizardDetailsStep() {
    const b = wizardBucket || {};
    const isSavings = norm(b.group) === "savings_goal";

    return (
      <div style={{ display: "grid", gap: 12 }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
          <div>
            <div style={{ fontSize: 12, color: COLORS.subtext, fontWeight: 700, marginBottom: 6 }}>Bucket name</div>
            <Input
              value={b.name || ""}
              onChange={(v) => setWizardBucket((prev) => ({ ...prev, name: v }))}
              placeholder="e.g. Groceries"
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: COLORS.subtext, fontWeight: 700, marginBottom: 6 }}>Status</div>
            <Select
              value={b.isActive === false ? "inactive" : "active"}
              onChange={(v) => setWizardBucket((prev) => ({ ...prev, isActive: v === "active" }))}
              options={[
                { value: "active", label: "Active" },
                { value: "inactive", label: "Off list (greyed out)" },
              ]}
            />
          </div>
        </div>

        {!isSavings ? (
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, color: COLORS.subtext, fontWeight: 700, marginBottom: 6 }}>
                Amount (per frequency)
              </div>
              <Input
                type="number"
                value={String(b.amount ?? "")}
                onChange={(v) => setWizardBucket((prev) => ({ ...prev, amount: v }))}
                placeholder="e.g. 600"
              />
            </div>
          </div>
        ) : (
          <Card style={{ padding: 14 }}>
            <div style={{ fontWeight: 800, color: COLORS.text }}>Savings goal</div>
            <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, color: COLORS.subtext, fontWeight: 700, marginBottom: 6 }}>Goal amount</div>
                <Input
                  type="number"
                  value={String(b.goalAmount ?? "")}
                  onChange={(v) => setWizardBucket((prev) => ({ ...prev, goalAmount: v }))}
                  placeholder="e.g. 5000"
                />
              </div>
              <div>
                <div style={{ fontSize: 12, color: COLORS.subtext, fontWeight: 700, marginBottom: 6 }}>Goal date</div>
                <Input
                  type="date"
                  value={b.goalDate || ""}
                  onChange={(v) => setWizardBucket((prev) => ({ ...prev, goalDate: v }))}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, color: COLORS.subtext, fontWeight: 700, marginBottom: 6 }}>Monthly amount</div>
                <Input
                  type="number"
                  value={String(b.monthlyAmount ?? "")}
                  onChange={(v) => setWizardBucket((prev) => ({ ...prev, monthlyAmount: v }))}
                  placeholder="e.g. 250"
                />
              </div>
            </div>
            <div style={{ marginTop: 10, fontSize: 12, color: COLORS.subtext }}>
              Enter any two fields. Trelli will calculate the third on save.
            </div>
          </Card>
        )}
      </div>
    );
  }

  function WizardScheduleStep() {
    const b = wizardBucket || {};
    const isSavings = norm(b.group) === "savings_goal";
    if (isSavings) {
      return (
        <Card style={{ padding: 14 }}>
          <div style={{ fontWeight: 800, color: COLORS.text }}>Schedule</div>
          <div style={{ marginTop: 8, fontSize: 12, color: COLORS.subtext }}>
            Savings goals allocate monthly. Reset windows don’t apply the same way.
          </div>
        </Card>
      );
    }

    const cadence = b.cadence || "monthly";
    const cadenceOptions = [
      { value: "weekly", label: "Weekly" },
      { value: "biweekly", label: "Bi-weekly" },
      { value: "semimonthly", label: "Twice per month (1st & 15th)" },
      { value: "monthly", label: "Monthly" },
      { value: "custom_monthly", label: "Custom monthly" },
    ];

    return (
      <div style={{ display: "grid", gap: 12 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <div style={{ fontSize: 12, color: COLORS.subtext, fontWeight: 700, marginBottom: 6 }}>Frequency</div>
            <Select
              value={cadence}
              onChange={(v) => setWizardBucket((prev) => ({ ...prev, cadence: v }))}
              options={cadenceOptions}
            />
          </div>

          <div>
            <div style={{ fontSize: 12, color: COLORS.subtext, fontWeight: 700, marginBottom: 6 }}>Anchor date</div>
            <Input
              type="date"
              value={(b.anchorDate || `${monthKey}-01`).slice(0, 10)}
              onChange={(v) => setWizardBucket((prev) => ({ ...prev, anchorDate: v }))}
              style={{ opacity: cadence === "weekly" || cadence === "biweekly" ? 1 : 0.5 }}
            />
            <div style={{ marginTop: 6, fontSize: 12, color: COLORS.subtext }}>
              Used for weekly/bi-weekly precision. Default is the 1st; Trelli refines later.
            </div>
          </div>
        </div>

        {cadence === "custom_monthly" ? (
          <Card style={{ padding: 14 }}>
            <div style={{ fontWeight: 800, color: COLORS.text }}>Custom amounts</div>
            <div style={{ marginTop: 8, fontSize: 12, color: COLORS.subtext }}>
              Set different monthly budgets. If a month is blank, Trelli falls back to “Amount”.
            </div>

            <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
              {monthOptions.slice(0, 12).reverse().map((opt) => {
                const k = opt.value;
                const val = wizardBucket?.customByMonth?.[k] ?? "";
                return (
                  <div key={k} style={{ display: "grid", gap: 6 }}>
                    <div style={{ fontSize: 12, color: COLORS.subtext, fontWeight: 700 }}>{k}</div>
                    <Input
                      type="number"
                      value={String(val)}
                      onChange={(v) =>
                        setWizardBucket((prev) => ({
                          ...prev,
                          customByMonth: { ...(prev.customByMonth || {}), [k]: v === "" ? "" : Number(v) },
                        }))
                      }
                      placeholder="—"
                    />
                  </div>
                );
              })}
            </div>
          </Card>
        ) : null}
      </div>
    );
  }

  function WizardPermissionsStep() {
    const b = wizardBucket || {};
    const p = bucketPerms(b);

    // Minimal, deterministic permission UI for now:
    // - show current uid chips + allow toggling "spouse view" and "spouse spend"
    // - you can expand later to a full people picker once you have household roster in UI
    const uid = user?.uid || "";
    const allowManageAll = canManageAll(member);

    const toggleUid = (field) => {
      setWizardBucket((prev) => {
        const perms = bucketPerms(prev);
        const set = new Set(perms[field] || []);
        if (set.has(uid)) set.delete(uid);
        else set.add(uid);
        return { ...prev, permissions: { ...perms, [field]: Array.from(set) } };
      });
    };

    return (
      <Card style={{ padding: 14 }}>
        <div style={{ fontWeight: 800, color: COLORS.text }}>Permissions</div>
        <div style={{ marginTop: 8, fontSize: 12, color: COLORS.subtext }}>
          Per-bucket permissions support spouse visibility without spend, and future card declines by spend access.
        </div>

        <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Card style={{ padding: 12 }}>
            <div style={{ fontSize: 12, color: COLORS.subtext, fontWeight: 700 }}>View</div>
            <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Button variant="soft" onClick={() => toggleUid("viewUids")}>
                Toggle me ({p.viewUids.includes(uid) ? "On" : "Off"})
              </Button>
            </div>
            <div style={{ marginTop: 10, fontSize: 12, color: COLORS.subtext }}>
              Current: {p.viewUids.length ? p.viewUids.join(", ") : "—"}
            </div>
          </Card>

          <Card style={{ padding: 12 }}>
            <div style={{ fontSize: 12, color: COLORS.subtext, fontWeight: 700 }}>Spend</div>
            <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Button variant="soft" onClick={() => toggleUid("spendUids")}>
                Toggle me ({p.spendUids.includes(uid) ? "On" : "Off"})
              </Button>
            </div>
            <div style={{ marginTop: 10, fontSize: 12, color: COLORS.subtext }}>
              Current: {p.spendUids.length ? p.spendUids.join(", ") : "—"}
            </div>
          </Card>

          <Card style={{ padding: 12 }}>
            <div style={{ fontSize: 12, color: COLORS.subtext, fontWeight: 700 }}>Manage</div>
            <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Button variant="soft" onClick={() => toggleUid("manageUids")}>
                Toggle me ({p.manageUids.includes(uid) ? "On" : "Off"})
              </Button>
              {!allowManageAll ? (
                <div style={{ fontSize: 12, color: COLORS.subtext, alignSelf: "center" }}>
                  Note: Heads/Admins can override later.
                </div>
              ) : null}
            </div>
            <div style={{ marginTop: 10, fontSize: 12, color: COLORS.subtext }}>
              Current: {p.manageUids.length ? p.manageUids.join(", ") : "—"}
            </div>
          </Card>

          <Card style={{ padding: 12 }}>
            <div style={{ fontSize: 12, color: COLORS.subtext, fontWeight: 700 }}>Approve bailout</div>
            <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Button variant="soft" onClick={() => toggleUid("approveBailoutUids")}>
                Toggle me ({p.approveBailoutUids.includes(uid) ? "On" : "Off"})
              </Button>
            </div>
            <div style={{ marginTop: 10, fontSize: 12, color: COLORS.subtext }}>
              Current: {p.approveBailoutUids.length ? p.approveBailoutUids.join(", ") : "—"}
            </div>
          </Card>
        </div>

        <div style={{ marginTop: 12, fontSize: 12, color: COLORS.subtext }}>
          Next upgrade: show household roster with toggles per person, including spouse view-all default and child restrictions.
        </div>
      </Card>
    );
  }

  function WizardFundingStep() {
    const b = wizardBucket || {};
    const fundingType = b.fundingType || "external_only";

    const opts = [
      { value: "external_only", label: "External only (tracking)" },
      { value: "account_bucket", label: "Trelli Account bucket (coming soon)", disabled: true },
      { value: "card_bucket", label: "Trelli Card bucket (coming soon)", disabled: true },
    ];

    return (
      <div style={{ display: "grid", gap: 12 }}>
        <Card style={{ padding: 14 }}>
          <div style={{ fontWeight: 800, color: COLORS.text }}>Funding</div>
          <div style={{ marginTop: 8, fontSize: 12, color: COLORS.subtext }}>
            External transactions reduce budgets when assigned to a bucket. Trelli cards/accounts will auto-assign later.
          </div>

          <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, color: COLORS.subtext, fontWeight: 700, marginBottom: 6 }}>Funding type</div>
              <Select
                value={fundingType}
                onChange={(v) => setWizardBucket((prev) => ({ ...prev, fundingType: v }))}
                options={opts}
              />
            </div>

            <div style={{ fontSize: 12, color: COLORS.subtext }}>
              External account linking UI will appear here once your Plaid account list is surfaced in Trelli.
            </div>
          </div>
        </Card>
      </div>
    );
  }

  function WizardReviewStep() {
    const b = wizardBucket || {};
    const isSavings = norm(b.group) === "savings_goal";

    const plannedPreview = isSavings
      ? Number(b.monthlyAmount ?? 0) || 0
      : computePlannedAmountForMonth(b, monthKey);

    return (
      <div style={{ display: "grid", gap: 12 }}>
        <Card style={{ padding: 14 }}>
          <div style={{ fontWeight: 800, color: COLORS.text }}>Review</div>

          <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div style={{ display: "grid", gap: 8 }}>
              <div style={{ color: COLORS.subtext, fontSize: 12 }}>Type</div>
              <div style={{ fontWeight: 700 }}>{b.group || "-"}</div>
            </div>

            <div style={{ display: "grid", gap: 8 }}>
              <div style={{ color: COLORS.subtext, fontSize: 12 }}>Name</div>
              <div style={{ fontWeight: 700 }}>{b.name || "-"}</div>
            </div>

            {!isSavings ? (
              <>
                <div style={{ display: "grid", gap: 8 }}>
                  <div style={{ color: COLORS.subtext, fontSize: 12 }}>Cadence</div>
                  <div style={{ fontWeight: 700 }}>{b.cadence || "-"}</div>
                </div>

                <div style={{ display: "grid", gap: 8 }}>
                  <div style={{ color: COLORS.subtext, fontSize: 12 }}>Amount (per frequency)</div>
                  <div style={{ fontWeight: 700 }}>{fmtMoney(Number(b.amount ?? 0) || 0)}</div>
                </div>
              </>
            ) : (
              <>
                <div style={{ display: "grid", gap: 8 }}>
                  <div style={{ color: COLORS.subtext, fontSize: 12 }}>Goal amount</div>
                  <div style={{ fontWeight: 700 }}>{fmtMoney(Number(b.goalAmount ?? 0) || 0)}</div>
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  <div style={{ color: COLORS.subtext, fontSize: 12 }}>Goal date</div>
                  <div style={{ fontWeight: 700 }}>{b.goalDate || "-"}</div>
                </div>
              </>
            )}

            <div style={{ display: "grid", gap: 8 }}>
              <div style={{ color: COLORS.subtext, fontSize: 12 }}>Planned this month (preview)</div>
              <div style={{ fontWeight: 900 }}>{fmtMoney(plannedPreview)}</div>
            </div>

            <div style={{ display: "grid", gap: 8 }}>
              <div style={{ color: COLORS.subtext, fontSize: 12 }}>Funding</div>
              <div style={{ fontWeight: 700 }}>{b.fundingType || "external_only"}</div>
            </div>
          </div>

          <div style={{ marginTop: 12, fontSize: 12, color: COLORS.subtext }}>
            Saving will: create/update the bucket in the selected plan, and upsert the month snapshot for {monthKey}.
          </div>
        </Card>

        {wizardErrors.length ? (
          <Card style={{ padding: 12, borderColor: "rgba(176,0,32,0.25)" }}>
            <div style={{ fontWeight: 800, color: COLORS.danger }}>Fix these before saving:</div>
            <ul style={{ marginTop: 8, marginLeft: 18, color: COLORS.danger, fontSize: 13 }}>
              {wizardErrors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </Card>
        ) : null}
      </div>
    );
  }

  function WizardModal() {
    const step = wizardStep;

    let body = null;
    if (step === 0) body = <WizardTypeStep />;
    if (step === 1) body = <WizardDetailsStep />;
    if (step === 2) body = <WizardScheduleStep />;
    if (step === 3) body = <WizardPermissionsStep />;
    if (step === 4) body = <WizardFundingStep />;
    if (step === 5) body = <WizardReviewStep />;

    const isLast = step === WIZ_STEPS.length - 1;
    const canNext = wizardErrors.length === 0 || step < 1; // allow early nav; review blocks save

    return (
      <Modal
        open={wizardOpen}
        title={wizardMode === "create" ? "Add budget item" : "Edit budget item"}
        onClose={() => setWizardOpen(false)}
        footer={
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div style={{ display: "flex", gap: 10 }}>
              <Button variant="ghost" onClick={() => setWizardOpen(false)}>
                Cancel
              </Button>
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Button variant="ghost" onClick={() => setWizardStep((s) => Math.max(0, s - 1))} disabled={step === 0}>
                Back
              </Button>
              {!isLast ? (
                <Button
                  variant="primary"
                  onClick={() => {
                    if (!canNext && step >= 1) return;
                    setWizardStep((s) => Math.min(WIZ_STEPS.length - 1, s + 1));
                  }}
                >
                  Next
                </Button>
              ) : (
                <Button variant="primary" onClick={saveWizard} disabled={wizardErrors.length > 0}>
                  Save
                </Button>
              )}
            </div>
          </div>
        }
      >
        <div style={{ display: "grid", gap: 14 }}>
          <WizardStepper />
          {body}
        </div>
      </Modal>
    );
  }

  /* ──────────────────────────────────────────────────────────────────────────────
     Drafts modal
  ─────────────────────────────────────────────────────────────────────────────── */
  function DraftsModal() {
    return (
      <Modal
        open={draftsOpen}
        title="Draft Budgets"
        onClose={() => setDraftsOpen(false)}
        footer={
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
            <div style={{ display: "flex", gap: 10 }}>
              <Button variant="soft" onClick={createDraftFromActive} disabled={!canManageAll(member) || !activePlan?.id}>
                Create draft from active
              </Button>
            </div>
            <Button variant="primary" onClick={() => setDraftsOpen(false)}>
              Done
            </Button>
          </div>
        }
      >
        <div style={{ display: "grid", gap: 12 }}>
          {draftPlans.length === 0 ? (
            <Card style={{ padding: 14 }}>
              <div style={{ fontWeight: 800, color: COLORS.text }}>No drafts yet</div>
              <div style={{ marginTop: 6, fontSize: 12, color: COLORS.subtext }}>
                Create a draft to collaborate, compare, and activate when ready.
              </div>
            </Card>
          ) : (
            draftPlans.map((d) => (
              <Card key={d.id} style={{ padding: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 900, color: COLORS.text }}>{d.title || `Draft ${d.id}`}</div>
                    <div style={{ marginTop: 6, fontSize: 12, color: COLORS.subtext }}>
                      Updated:{" "}
                      {d.updatedAt?.toDate ? d.updatedAt.toDate().toLocaleString() : "—"} | Created by: {d.createdBy || "—"}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setPlanStatus("draft");
                        setPlanId(d.id);
                        setDraftsOpen(false);
                      }}
                    >
                      Open
                    </Button>
                    <Button
                      variant="primary"
                      disabled={!canManageAll(member) || activating}
                      onClick={() => activateDraft(d.id)}
                      title={!canManageAll(member) ? "Only heads/admins can activate drafts." : ""}
                    >
                      {activating ? "Activating…" : "Activate"}
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </Modal>
    );
  }

  /* ──────────────────────────────────────────────────────────────────────────────
     Layout
  ─────────────────────────────────────────────────────────────────────────────── */
  const householdOptions = useMemo(() => {
    return households.map((h) => ({
      value: h.id,
      label: `${h.name}${h.type === "child" ? " (Child)" : ""} — ${h.memberRole}`,
    }));
  }, [households]);

  const planOptions = useMemo(() => {
    const opts = [];
    if (activePlan?.id) opts.push({ value: activePlan.id, label: "Active plan" });
    for (const d of draftPlans) opts.push({ value: d.id, label: `Draft: ${d.title || d.id}` });
    return opts;
  }, [activePlan, draftPlans]);

  // Derive current plan status label
  useEffect(() => {
    if (!planId) return;
    if (activePlan?.id && planId === activePlan.id) {
      setPlanStatus("active");
      return;
    }
    const isDraft = draftPlans.some((d) => d.id === planId);
    if (isDraft) setPlanStatus("draft");
  }, [planId, activePlan?.id, draftPlans]);

  const topStickyHeight = 146; // used by sticky headers below

  if (!user) {
    return <div style={{ padding: 16 }}>Please sign in.</div>;
  }

  return (
    <div style={{ padding: 16, maxWidth: 1280, margin: "0 auto", color: COLORS.text }}>
      {/* Header + controls sticky zone */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 60,
          background: "#fff",
          borderBottom: `1px solid ${COLORS.border}`,
          paddingBottom: 12,
        }}
      >
        {/* Row 1: Household + Plan + Month */}
        <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1.2fr 1fr auto", gap: 12, alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 12, color: COLORS.subtext, fontWeight: 700, marginBottom: 6 }}>Household</div>
            <Select value={householdId} onChange={setHouseholdId} options={householdOptions.length ? householdOptions : [{ value: "", label: "—" }]} />
          </div>

          <div>
            <div style={{ fontSize: 12, color: COLORS.subtext, fontWeight: 700, marginBottom: 6 }}>Plan</div>
            <Select value={planId} onChange={setPlanId} options={planOptions.length ? planOptions : [{ value: "", label: "No active plan" }]} />
          </div>

          <div>
            <div style={{ fontSize: 12, color: COLORS.subtext, fontWeight: 700, marginBottom: 6 }}>Month</div>
            <Select value={monthKey} onChange={setMonthKey} options={monthOptions.map((m) => ({ value: m.value, label: m.label }))} />
          </div>

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", alignSelf: "end" }}>
            <Button variant="soft" onClick={() => setDraftsOpen(true)}>
              Draft budgets
            </Button>
            <Button variant="primary" onClick={openCreateWizard} disabled={!planId}>
              Add budget item
            </Button>
          </div>
        </div>

        {/* Row 2: Summary */}
        <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
          <Card style={{ padding: 12, cursor: "pointer" }} onClick={() => scrollToSection("income")}>
            <div style={{ fontSize: 12, color: COLORS.subtext, fontWeight: 800 }}>Income</div>
            <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <div style={{ fontSize: 18, fontWeight: 900 }}>{fmtMoney(summaryTotals.incomePlanned)}</div>
              <div style={{ fontSize: 12, color: COLORS.subtext }}>Planned</div>
            </div>
            <div style={{ marginTop: 6, fontSize: 12, color: COLORS.subtext }}>
              {fmtMoney(summaryTotals.incomeActual)} received
            </div>
          </Card>

          <Card style={{ padding: 12, cursor: "pointer" }} onClick={() => scrollToSection("savings")}>
            <div style={{ fontSize: 12, color: COLORS.subtext, fontWeight: 800 }}>Savings Goals</div>
            <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <div style={{ fontSize: 18, fontWeight: 900 }}>{fmtMoney(summaryTotals.savingsPlanned)}</div>
              <div style={{ fontSize: 12, color: COLORS.subtext }}>Planned</div>
            </div>
            <div style={{ marginTop: 6, fontSize: 12, color: COLORS.subtext }}>
              {fmtMoney(summaryTotals.savingsActual)} allocated
            </div>
          </Card>

          <Card style={{ padding: 12, cursor: "pointer" }} onClick={() => scrollToSection("expense")}>
            <div style={{ fontSize: 12, color: COLORS.subtext, fontWeight: 800 }}>Expenses</div>
            <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <div style={{ fontSize: 18, fontWeight: 900 }}>{fmtMoney(summaryTotals.expensePlanned)}</div>
              <div style={{ fontSize: 12, color: COLORS.subtext }}>Planned</div>
            </div>
            <div style={{ marginTop: 6, fontSize: 12, color: COLORS.subtext }}>
              {fmtMoney(summaryTotals.expenseActual)} spent
            </div>
          </Card>
        </div>

        {/* Row 3: Search */}
        <div style={{ marginTop: 12, position: "relative", display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
          <div>
            <Input
              value={search}
              onChange={(v) => {
                setSearch(v);
                setSearchOpen(true);
              }}
              placeholder="Search budget item…"
              style={{ paddingRight: 12 }}
              onFocus={() => setSearchOpen(true)}
            />
            {searchOpen && searchResults.length > 0 ? (
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  top: 44,
                  background: "#fff",
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 12,
                  boxShadow: "0 14px 30px rgba(0,0,0,0.10)",
                  zIndex: 200,
                  overflow: "hidden",
                }}
                onMouseLeave={() => setSearchOpen(false)}
              >
                {searchResults.map((b) => (
                  <div
                    key={b.id}
                    style={{
                      padding: "10px 12px",
                      borderBottom: `1px solid ${COLORS.border}`,
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                    }}
                    onClick={() => {
                      setDetailBucket(b);
                      setDetailOpen(true);
                      setSearchOpen(false);
                      setSearch("");
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>{b.name}</div>
                    <div style={{ fontSize: 12, color: COLORS.subtext }}>{b.group}</div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>

        {/* Error */}
        {error ? (
          <div style={{ marginTop: 10, color: COLORS.danger, fontSize: 13, fontWeight: 650 }}>{error}</div>
        ) : null}
      </div>

      {/* Mass actions bar */}
      {selectedIds.length > 0 ? (
        <div
          style={{
            position: "sticky",
            top: topStickyHeight + 10,
            zIndex: 55,
            marginTop: 12,
            marginBottom: 12,
          }}
        >
          <Card style={{ padding: 12, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 800 }}>
              {selectedIds.length} selected
              <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.subtext, marginLeft: 8 }}>
                (Mass actions apply to the selected plan)
              </span>
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <Button variant="ghost" onClick={exportSelectedCSV}>
                Export CSV
              </Button>
              <Button variant="ghost" onClick={() => setTempIncOpen(true)} disabled={!canManageAll(member)}>
                Temporary increase
              </Button>
              <Button variant="danger" onClick={archiveSelected} disabled={!canManageAll(member)}>
                Delete (archive)
              </Button>
              <Button variant="ghost" onClick={clearSelected}>
                Clear
              </Button>
            </div>
          </Card>
        </div>
      ) : null}

      {/* Scroll area */}
      <div style={{ marginTop: 12 }}>
        {loading ? <div style={{ padding: 12 }}>Loading…</div> : null}

        <Section
          title="Income"
          sectionKey="income"
          headerCols="44px 2.1fr 2.0fr 1.1fr 1.1fr 1.5fr 1.4fr"
          stickyRef={incomeRef}
        >
          {decorated.income.length === 0 ? (
            <div style={{ padding: 12, color: COLORS.subtext }}>No income buckets yet.</div>
          ) : (
            decorated.income.map((b) => <BucketRow key={b.id} bucket={b} variant="income" />)
          )}
        </Section>

        <Section
          title="Savings Goals"
          sectionKey="savings"
          headerCols="44px 2.1fr 2.0fr 1.1fr 1.1fr 1.5fr 1.4fr"
          stickyRef={savingsRef}
        >
          {decorated.savings.length === 0 ? (
            <div style={{ padding: 12, color: COLORS.subtext }}>No savings goals yet.</div>
          ) : (
            decorated.savings.map((b) => <BucketRow key={b.id} bucket={b} variant="savings" />)
          )}
        </Section>

        <Section
          title="Expenses"
          sectionKey="expense"
          headerCols="44px 2.1fr 2.0fr 1.1fr 1.1fr 1.5fr 1.4fr"
          stickyRef={expenseRef}
        >
          {decorated.expense.length === 0 ? (
            <div style={{ padding: 12, color: COLORS.subtext }}>No expense buckets yet.</div>
          ) : (
            decorated.expense.map((b) => <BucketRow key={b.id} bucket={b} variant="expense" />)
          )}
        </Section>
      </div>

      {/* Modals */}
      <WizardModal />
      <DetailModal />
      <DraftsModal />

      {/* Temp Increase Modal */}
      <Modal
        open={tempIncOpen}
        title="Temporary increase (selected buckets)"
        onClose={() => setTempIncOpen(false)}
        footer={
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
            <Button variant="ghost" onClick={() => setTempIncOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={applyTempIncrease} disabled={!canManageAll(member)}>
              Apply
            </Button>
          </div>
        }
      >
        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ fontSize: 12, color: COLORS.subtext }}>
            Applies a month-only override to planned amount (stored in the month snapshot). This does not change the bucket definition.
          </div>
          <div>
            <div style={{ fontSize: 12, color: COLORS.subtext, fontWeight: 700, marginBottom: 6 }}>Increase amount</div>
            <Input type="number" value={tempIncAmount} onChange={setTempIncAmount} placeholder="e.g. 50" />
          </div>
        </div>
      </Modal>
    </div>
  );
}